import {
	CheckCircle as CheckCircleIcon,
	CircleOutlined as CircleOutlinedIcon,
	Visibility as VisibilityIcon,
	VisibilityOff as VisibilityOffIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Grid2, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { AGDialog } from "../../../components/AGDialog";
import { useNotification } from "../../../components/FeedbackNotification/useNotification";
import { useChangeUserPasswordMutation } from "../../../redux/reducers/userManagement.reducer";
import { VALIDATION_REGEX } from "../../../utils/constants";

interface IChangePasswordDialogProps {
	open: boolean;
	userId: number;
	onClose: () => void;
}

interface IPasswordStrength {
	value: number;
	color: string;
	details: ("length" | "case" | "chars")[];
}

interface IChangeUserPasswordInputs {
	// current_password: string;
	new_password: string;
	repeat_password: string;
}

const ChangePasswordDialog: FC<IChangePasswordDialogProps> = (props) => {
	const { open, userId, onClose } = props;

	const { notify } = useNotification();

	const [showInput, setShowInput] = useState<{ [key in keyof IChangeUserPasswordInputs]: boolean }>({
		// current_password: false,
		new_password: false,
		repeat_password: false
	});

	// APIS
	// CHANGE USER PASSWORD
	const [
		changeUserPassword,
		{ isLoading: changeUserPasswordLoading, error: changeUserPasswordError, reset: resetChangeUserPasswordState }
	] = useChangeUserPasswordMutation();

	const {
		reset,
		watch,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<IChangeUserPasswordInputs>();

	const password = watch("new_password") ?? "";

	const passwordStrength = useMemo<IPasswordStrength>(() => {
		let strength: IPasswordStrength["details"] = [];

		if (password.length >= 8) strength.push("length");
		if (VALIDATION_REGEX.PASSWORD.CASE.test(password)) strength.push("case");
		if (VALIDATION_REGEX.PASSWORD.SPECIAL_CHAR.test(password)) strength.push("chars");

		return {
			value: (strength.length * 100) / 3,
			color:
				strength.length <= 1
					? "var(--color-error-main)"
					: strength.length === 2
					? "var(--color-warning-main)"
					: "var(--color-success-main)",
			details: strength
		};
	}, [password]);

	function toggleInputVisibility(key: keyof IChangeUserPasswordInputs) {
		setShowInput((prev) => ({
			...prev,
			[key]: !prev[key]
		}));
	}

	function handleSubmitForm(formData: IChangeUserPasswordInputs) {
		changeUserPassword({
			id: userId,
			data: {
				password: formData.new_password,
				password2: formData.repeat_password
			}
		})
			.unwrap()
			.then(() => {
				notify("Password changed successfully", "success");
				onClose();
			})
			.catch(() => {});
	}

	useEffect(() => {
		if (open) {
			reset({
				// current_password: "",
				new_password: "",
				repeat_password: ""
			});
		} else {
			resetChangeUserPasswordState();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	return (
		<AGDialog open={open} onClose={onClose} title="Change Password" color="primary" maxWidth="xs">
			<Box component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12 }}>
						<TextField
							required
							fullWidth
							size="small"
							label="New Password"
							autoComplete="off"
							type={showInput.new_password ? "text" : "password"}
							error={!!errors.new_password}
							helperText={errors.new_password?.message?.toString() || ""}
							slotProps={{
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<IconButton edge="end" tabIndex={-1} onClick={() => toggleInputVisibility("new_password")}>
												{showInput.new_password ? <VisibilityOffIcon /> : <VisibilityIcon />}
											</IconButton>
										</InputAdornment>
									)
								}
							}}
							{...register("new_password", {
								required: "New password is required",
								pattern: {
									value: VALIDATION_REGEX.PASSWORD.COMPLETE,
									message: "Password should contain at least one alphabet, one number and a special character"
								},
								minLength: {
									value: 8,
									message: "Password should be at least 8 characters long"
								}
							})}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<TextField
							required
							fullWidth
							size="small"
							label="Confirm Password"
							autoComplete="off"
							type={showInput.repeat_password ? "text" : "password"}
							error={!!errors.repeat_password}
							helperText={errors.repeat_password?.message?.toString() || ""}
							slotProps={{
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<IconButton edge="end" tabIndex={-1} onClick={() => toggleInputVisibility("repeat_password")}>
												{showInput.repeat_password ? <VisibilityOffIcon /> : <VisibilityIcon />}
											</IconButton>
										</InputAdornment>
									)
								}
							}}
							{...register("repeat_password", {
								required: "Confirm password is required",
								validate: (value, { new_password }) => (value !== new_password ? "Passwords do not match" : true)
							})}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Box sx={{ width: "100%", height: 4, bgcolor: "#E0E0E0", borderRadius: 2, overflow: "hidden" }}>
								<Box
									sx={{
										width: `${passwordStrength.value}%`,
										minWidth: "5%",
										height: 4,
										bgcolor: passwordStrength.color,
										transition: "width ease-out 0.5s"
									}}
								/>
							</Box>
						</Box>

						<Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.5rem" }}>
							{passwordStrength.details.includes("length") ? (
								<CheckCircleIcon color="success" sx={{ fontSize: 14 }} />
							) : (
								<CircleOutlinedIcon color="disabled" sx={{ fontSize: 14 }} />
							)}

							<Typography
								variant="body2"
								color={passwordStrength.details.includes("length") ? "success.main" : "text.secondary"}
							>
								At least 8 characters
							</Typography>
						</Box>

						<Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.25rem" }}>
							{passwordStrength.details.includes("case") ? (
								<CheckCircleIcon color="success" sx={{ fontSize: 14 }} />
							) : (
								<CircleOutlinedIcon color="disabled" sx={{ fontSize: 14 }} />
							)}

							<Typography
								variant="body2"
								color={passwordStrength.details.includes("case") ? "success.main" : "text.secondary"}
							>
								Include uppercase & lowercase letters
							</Typography>
						</Box>

						<Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.25rem" }}>
							{passwordStrength.details.includes("chars") ? (
								<CheckCircleIcon color="success" sx={{ fontSize: 14 }} />
							) : (
								<CircleOutlinedIcon color="disabled" sx={{ fontSize: 14 }} />
							)}

							<Typography
								variant="body2"
								color={passwordStrength.details.includes("chars") ? "success.main" : "text.secondary"}
							>
								Include numbers & special characters
							</Typography>
						</Box>
					</Grid2>

					{changeUserPasswordError ? (
						<Grid2 size={{ xs: 12 }}>
							<Typography variant="body2" color="error" textAlign="center" marginTop="1rem">
								{"data" in changeUserPasswordError && changeUserPasswordError.data
									? String(changeUserPasswordError.data)
									: "Failed to change password"}
							</Typography>
						</Grid2>
					) : null}

					<Grid2 size={{ xs: 12 }}>
						<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
							<LoadingButton
								disableElevation
								variant="contained"
								color="primary"
								type="submit"
								loading={changeUserPasswordLoading}
							>
								Change Password
							</LoadingButton>
						</Box>
					</Grid2>
				</Grid2>
			</Box>
		</AGDialog>
	);
};

export default ChangePasswordDialog;
